import React, { useEffect, useState, useRef } from "react";
import { ResultRating } from "@/components/ResultRating";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Loader2, TrendingUp, TrendingDown, Target, AlertTriangle,
  Palette, FileText, Users, Layers, RefreshCw, Wand2, Lock, CreditCard,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import NoProject from "@/components/NoProject";
import JourneyBar from "@/components/JourneyBar";
import { toast } from "sonner";
import { BrandDocButton } from "@/components/BrandDocButton";
import { LogoStudio } from "@/components/LogoStudio";
import { ColorDetailDialog } from "@/components/ColorDetailDialog";
import { StageNav } from "@/components/StageNav";

const PREF_OPTIONS = {
  "الهدف التسويقي": ["زيادة المبيعات", "زيادة الوعي بالعلامة", "جذب متابعين جدد", "إطلاق منتج جديد", "تعزيز الولاء"],
  "نبرة العلامة": ["فاخرة", "ودية", "احترافية", "شبابية حماسية"],
  "مستوى الميزانية": ["محدودة", "متوسطة", "مرتفعة"],
  "القناة الأساسية": ["Instagram", "TikTok", "Snapchat", "X (تويتر)"],
  "نوع المحتوى المفضل": ["صور", "فيديو", "مزيج متوازن"],
};

const swotMeta = {
  strengths: { title: "نقاط القوة", icon: TrendingUp, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
  weaknesses: { title: "نقاط الضعف", icon: TrendingDown, color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/20" },
  opportunities: { title: "الفرص", icon: Target, color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20" },
  threats: { title: "التهديدات", icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
};

export default function StrategyGenerator() {
  const { activeProject, refresh } = useProjects();
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [logo, setLogo] = useState(null);
  const [approved, setApproved] = useState(false);
  const [approving, setApproving] = useState(false);
  const [prefs, setPrefs] = useState({});
  const pollRef = useRef(null);
  const [recoloring, setRecoloring] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [colorIdx, setColorIdx] = useState(null);

  const updateColorHex = (i, hex) => {
    setStrategy((prev) => {
      const colors = [...((prev.visual_identity || {}).colors || [])];
      colors[i] = { ...colors[i], hex };
      return { ...prev, visual_identity: { ...(prev.visual_identity || {}), colors } };
    });
  };

  const saveColors = async () => {
    setSavingColors(true);
    try {
      const colors = (strategy.visual_identity || {}).colors || [];
      await api.put(`/projects/${activeProject.id}/colors`, { colors });
      const r = await api.get(`/projects/${activeProject.id}/strategy`);
      if (r.data?.data) setStrategy(r.data.data);
      await refresh();
      toast.success("تم حفظ ألوان الهوية ✅");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingColors(false);
    }
  };

  const reSuggestColors = async () => {
    setRecoloring(true);
    try {
      const { data } = await api.post(`/projects/${activeProject.id}/colors/suggest`);
      if (data.colors?.length) {
        setStrategy((prev) => ({
          ...prev,
          visual_identity: { ...(prev.visual_identity || {}), colors: data.colors },
        }));
        toast.success("تم اقتراح لوحة ألوان جديدة 🎨");
      }
    } catch (err) {
      toast.error("تعذّر اقتراح ألوان جديدة");
    } finally {
      setRecoloring(false);
    }
  };

  const poll = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${activeProject.id}/strategy/status`);
        if (data.status === "completed" && data.data) {
          clearInterval(pollRef.current);
          setStrategy(data.data);
          setLoading(false);
          await refresh();
          toast.success("تم توليد الاستراتيجية بنجاح ✨");
        } else if (data.status === "failed") {
          clearInterval(pollRef.current);
          setLoading(false);
          toast.error(data.error || "فشل التوليد، حاول مرة أخرى");
        }
      } catch (e) {}
    }, 4000);
  };

  const approveStrategy = async () => {
    setApproving(true);
    try {
      await api.put(`/projects/${activeProject.id}/strategy/approve`);
      setApproved(true);
      toast.success("اعتُمدت الاستراتيجية — انتقل لتحديد الأهداف");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "تعذّر الاعتماد");
    } finally {
      setApproving(false);
    }
  };

  useEffect(() => {
    clearInterval(pollRef.current);
    if (!activeProject) { setFetching(false); return; }
    setFetching(true);
    setStrategy(null);
    setLoading(false);
    setLogo(activeProject.logo || null);
    api.get(`/projects/${activeProject.id}/strategy`)
      .then((r) => {
        setApproved(!!r.data?.approved);
        if (r.data?.data) setStrategy(r.data.data);
        else if (r.data?.status === "processing") { setLoading(true); poll(); }
      })
      .catch(() => {})
      .finally(() => setFetching(false));
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject]);

  // اكتمال المدخلات شرط لإظهار زر التوليد
  const missingInputs = [
    ...(!activeProject?.name ? ["اسم المشروع"] : []),
    ...(!activeProject?.business_type ? ["نوع النشاط"] : []),
    ...(!(activeProject?.description || "").trim() ? ["وصف المشروع"] : []),
    ...(!(activeProject?.target_audience || "").trim() ? ["الجمهور المستهدف"] : []),
    ...Object.keys(PREF_OPTIONS).filter((q) => !prefs[q]),
  ];
  const inputsComplete = missingInputs.length === 0;

  const generate = async (notes = "") => {
    if (!inputsComplete) { toast.error(`أكمل المدخلات أولاً: ${missingInputs.join("، ")}`); return; }
    setLoading(true);
    setStrategy(null);
    try {
      await api.post(`/projects/${activeProject.id}/strategy`, { preferences: prefs, notes: typeof notes === "string" ? notes : "" });
      toast.info("بدأ توليد الاستراتيجية… قد يستغرق حتى دقيقتين");
      poll();
    } catch (err) {
      setLoading(false);
      toast.error(formatError(err.response?.data?.detail) || "فشل بدء التوليد");
    }
  };

  const applyName = async (name) => {
    try {
      await api.patch(`/projects/${activeProject.id}/meta`, { name });
      await refresh();
      toast.success(`تم اعتماد الاسم: ${name}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (!activeProject) return <NoProject message="اختر أو أنشئ مشروعاً لتوليد استراتيجيته التسويقية." />;

  if (activeProject.has_strategy_ready) {
    return (
      <div className="space-y-6" data-testid="strategy-page">
        <JourneyBar current="strategy" />
        <div className="glass rounded-2xl p-10 text-center border border-amber-500/30 bg-amber-500/5 max-w-2xl mx-auto" data-testid="strategy-locked">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/15 text-amber-400 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7" />
          </div>
          <h1 className="text-2xl font-extrabold font-heading mb-2">توليد الاستراتيجية غير متاح لهذا المشروع</h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            مشروع «{activeProject.name}» يملك شعاراً واستراتيجية جاهزة بالفعل. توليد استراتيجية جديدة
            بالذكاء الاصطناعي ميزة مدفوعة تتطلب الاشتراك في <b>باقة استراتيجية</b>. يمكنك متابعة المحتوى والحملات والتنفيذ كالمعتاد.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button onClick={() => navigate("/app/packages")} className="bg-primary hover:bg-primary/90 text-white gap-2 font-bold" data-testid="strategy-pay-btn">
              <CreditCard className="w-4 h-4" /> اشترك في باقة استراتيجية
            </Button>
            <Button onClick={() => navigate("/app/studio")} variant="outline" className="border-border gap-2" data-testid="strategy-go-studio-btn">
              <Wand2 className="w-4 h-4" /> الانتقال لاستوديو المحتوى
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const s = strategy;

  return (
    <div className="space-y-6" data-testid="strategy-page">
      <JourneyBar current="strategy" onRegenerate={(notes) => generate(notes)} regenerating={loading} />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold font-heading">مولّد الاستراتيجية</h1>
          <p className="text-muted-foreground">استراتيجية متكاملة لـ «{activeProject.name}»</p>
        </div>
        {s && (
          <Button onClick={() => generate("")} disabled={loading} className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="strategy-generate-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            إعادة التوليد
          </Button>
        )}
      </div>

      {!s && !fetching && !loading && (
        <div className="glass rounded-2xl p-6 border border-border" data-testid="strategy-prefs">
          <h2 className="font-bold mb-1">أسئلة تفضيلية لتحليل توجهك</h2>
          <p className="text-xs text-muted-foreground mb-4">اختر إجاباتك لنبني استراتيجية مخصّصة لتوجهك التسويقي.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.keys(PREF_OPTIONS).map((q, idx) => (
              <div key={q}>
                <Label className="mb-2 block text-sm">{q}</Label>
                <Select value={prefs[q] || ""} onValueChange={(v) => setPrefs({ ...prefs, [q]: v })}>
                  <SelectTrigger className="bg-secondary border-border" data-testid={`pref-select-${idx}`}><SelectValue placeholder="اختر" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {PREF_OPTIONS[q].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          {!inputsComplete && (
            <p className="mt-4 text-xs text-destructive" data-testid="strategy-missing-inputs">
              يظهر زر التوليد بعد اكتمال المدخلات — المتبقي: {missingInputs.join("، ")}
              {missingInputs.some((m) => ["اسم المشروع", "نوع النشاط", "وصف المشروع", "الجمهور المستهدف"].includes(m)) && (
                <> · <Link to="/app/projects" className="underline">أكمل بيانات المشروع</Link></>
              )}
            </p>
          )}
          <Button onClick={() => generate("")} disabled={loading || !inputsComplete} className="w-full mt-5 bg-primary hover:bg-primary/90 text-white gap-2 font-bold h-12 disabled:opacity-40" data-testid="strategy-generate-bottom-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {inputsComplete ? "توليد الاستراتيجية" : "أكمل جميع المدخلات لتفعيل التوليد"}
          </Button>
        </div>
      )}

      {fetching && (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      )}

      {loading && !s && (
        <div className="glass rounded-2xl p-16 text-center border border-border">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">يقوم الذكاء الاصطناعي بتحليل مشروعك وبناء الاستراتيجية…</p>
        </div>
      )}

      {!fetching && !s && !loading && (
        <div className="glass rounded-2xl p-16 text-center border border-border" data-testid="strategy-empty">
          <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">لم يتم توليد استراتيجية بعد. اضغط «توليد الاستراتيجية» للبدء.</p>
        </div>
      )}

      {s && !loading && (
        <div className="space-y-6" data-testid="strategy-result">
          {s.executive_summary && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 border border-border">
              <div className="flex items-center gap-2 mb-3"><FileText className="w-5 h-5 text-primary" /><h2 className="text-lg font-bold">الملخص التنفيذي</h2></div>
              <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{s.executive_summary}</p>
            </motion.div>
          )}

          {s.swot && (
            <div>
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Layers className="w-5 h-5 text-primary" /> تحليل SWOT</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {Object.keys(swotMeta).map((k) => {
                  const m = swotMeta[k];
                  const items = s.swot[k] || [];
                  return (
                    <div key={k} className={`rounded-2xl p-5 border ${m.bg}`} data-testid={`swot-${k}`}>
                      <div className={`flex items-center gap-2 mb-3 font-bold ${m.color}`}><m.icon className="w-5 h-5" /> {m.title}</div>
                      <ul className="space-y-2">
                        {items.map((it, i) => <li key={i} className="text-sm flex gap-2"><span className={m.color}>•</span> {it}</li>)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {s.market_analysis && (
            <div className="glass rounded-2xl p-6 border border-border">
              <div className="flex items-center gap-2 mb-3"><Target className="w-5 h-5 text-primary" /><h2 className="text-lg font-bold">تحليل السوق</h2></div>
              <p className="text-sm leading-relaxed text-muted-foreground">{s.market_analysis}</p>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            {s.visual_identity && (
              <div className="glass rounded-2xl p-6 border border-border">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2"><Palette className="w-5 h-5 text-accent" /><h2 className="text-lg font-bold">الهوية المرئية</h2></div>
                  <Button size="sm" variant="outline" onClick={reSuggestColors} disabled={recoloring} className="border-accent/40 text-accent gap-1 h-8 text-xs" data-testid="resuggest-colors-btn">
                    {recoloring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} إعادة اقتراح الألوان
                  </Button>
                </div>
                {s.visual_identity.colors && (
                  <>
                    <p className="text-xs text-muted-foreground mb-2">انقر على أي لون لعرض تفاصيله الكاملة (HEX، RGB، CMYK، التباين، الاستخدام) أو تعديله، ثم احفظ الهوية.</p>
                    <div className="flex gap-3 mb-3 flex-wrap">
                      {s.visual_identity.colors.map((c, i) => (
                        <div key={i} className="text-center">
                          <button type="button" onClick={() => setColorIdx(i)} className="block cursor-pointer hover:scale-105 transition-transform" data-testid={`color-swatch-${i}`} title="عرض تفاصيل اللون">
                            <div className="w-14 h-14 rounded-xl border border-border mb-1" style={{ backgroundColor: c.hex }} />
                          </button>
                          <div className="text-xs text-muted-foreground">{c.name}</div>
                          <input type="text" value={c.hex}
                            onChange={(e) => updateColorHex(i, e.target.value)}
                            className="w-16 text-[10px] font-mono text-center bg-secondary border border-border rounded px-1 py-0.5 mt-0.5"
                            dir="ltr" data-testid={`color-hex-${i}`} />
                        </div>
                      ))}
                    </div>
                    <ColorDetailDialog color={colorIdx !== null ? s.visual_identity.colors[colorIdx] : null} index={colorIdx}
                      open={colorIdx !== null} onOpenChange={(o) => !o && setColorIdx(null)} onChangeHex={updateColorHex} />
                    <div className="flex gap-2 flex-wrap mb-4">
                      <Button size="sm" onClick={saveColors} disabled={savingColors}
                        className="gap-1 bg-primary/90 hover:bg-primary text-white h-8 text-xs" data-testid="save-colors-btn">
                        {savingColors ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Palette className="w-3.5 h-3.5" />} حفظ ألوان الهوية
                      </Button>
                      <BrandDocButton project={activeProject} testId="export-brand-doc"
                        className="bg-accent hover:bg-accent/90 text-white h-8 text-xs" />
                    </div>
                  </>
                )}

                {s.tagline && <p className="text-base text-accent font-bold mt-4" data-testid="strategy-tagline">“{s.tagline}”</p>}

                {s.brand_name_suggestions?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs text-muted-foreground mb-2">أسماء تجارية مقترحة (انقر للاعتماد):</div>
                    <div className="flex flex-wrap gap-2">
                      {s.brand_name_suggestions.map((n, i) => (
                        <button key={i} onClick={() => applyName(n)} data-testid={`brand-name-suggestion-${i}`}
                          className="text-sm bg-secondary hover:bg-primary/15 hover:text-primary border border-border px-3 py-1.5 rounded-full transition-colors">
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-5 pt-5 border-t border-border">
                  <LogoStudio projectId={activeProject.id} vi={s.visual_identity} logo={logo} setLogo={setLogo} refresh={refresh}
                    onIdentityChange={(vi) => setStrategy((prev) => ({ ...prev, visual_identity: vi }))} />
                </div>
              </div>
            )}

            <div className="space-y-4">
              {s.target_personas && (
                <div className="glass rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-primary" /><h2 className="text-lg font-bold">شرائح الجمهور</h2></div>
                  <div className="space-y-3">
                    {s.target_personas.map((p, i) => (
                      <div key={i} className="bg-secondary rounded-xl p-3">
                        <div className="font-semibold text-sm">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {s.content_pillars && (
                <div className="glass rounded-2xl p-6 border border-border">
                  <h2 className="text-lg font-bold mb-3">محاور المحتوى</h2>
                  <div className="flex flex-wrap gap-2">
                    {s.content_pillars.map((p, i) => <span key={i} className="text-sm bg-primary/15 text-primary px-3 py-1.5 rounded-full">{p}</span>)}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="glass rounded-2xl p-6 border border-primary/30 emerald-glow" data-testid="strategy-cta">
            <h2 className="text-lg font-bold mb-1">{approved ? "الاستراتيجية معتمدة — المرحلة التالية: الأهداف" : "راجع الاستراتيجية ثم اعتمدها للانتقال إلى الأهداف"}</h2>
            <p className="text-sm text-muted-foreground mb-4">المرحلة التالية: حدّد الأهداف ومؤشرات الأداء (KPIs) الشهرية بناءً على هذه الاستراتيجية.</p>
            <StageNav approved={approved} saving={approving} onApprove={approveStrategy} approveLabel="اعتمد الاستراتيجية" approvedLabel="الاستراتيجية معتمدة" nextLabel="التالي: الأهداف والمؤشرات" nextTo="/app/goals" testId="strategy" />
            <ResultRating product="strategy" contentId={activeProject?.id || ""} projectId={activeProject?.id || ""} className="mt-4" />
          </div>
        </div>
      )}
    </div>
  );
}
